<script setup>
import { computed } from 'vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';
import {
  isRevokedMessage,
  lifecycleFilletTone,
} from 'dashboard/helper/messageLifecycle';
import MessageLifecycle from './MessageLifecycle.vue';

// SiteUp V2 (F1.7): a linha da hora NÃO mora mais aqui dentro. Sobre a bolha
// colorida (azul do agente) o cinza neutro do texto sumia, então o MessageMeta
// passou a ser renderizado pelo Message.vue, na área [grid-area:meta], logo
// abaixo da bolha e sobre o fundo do app.
const { variant, orientation, inReplyTo, contentAttributes, createdAt } =
  useMessageContext();
const { t } = useI18n();

const varaintBaseMap = {
  // O `!` não é preguiça: o link renderizado no corpo da mensagem sai com a
  // classe `.link`, cuja regra global tem especificidade maior que a do
  // `[&_a]` do bubble. Sem o important o link ficava rgb(39,129,246) sobre
  // bolha rgb(29,155,240), medido no staging, praticamente invisível.
  [MESSAGE_VARIANTS.AGENT]:
    'bg-n-blue-9 text-white [&_a]:!text-white [&_a]:underline [&_.prose]:!text-white [&_.prose_strong]:!text-white [&_.prose_b]:!text-white [&_.prose_code]:!text-white [&_.prose_em]:!text-white [&_.prose_li]:!text-white [&_.prose_h1]:!text-white [&_.prose_h2]:!text-white [&_.prose_h3]:!text-white [&_.prose_blockquote]:!text-white [&_.prose_th]:!text-white',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-amber-2 text-n-slate-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'text-n-slate-11 text-xs',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]:
    'bg-n-slate-4 border border-n-weak text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]:
    'bg-transparent border border-n-ruby-6 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-slate-3 border border-dashed border-n-strong text-n-slate-11',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-[var(--workspace-radius,20px)] ltr:rounded-bl-[5px] rtl:rounded-br-[5px]',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-[var(--workspace-radius,20px)] ltr:rounded-br-[5px] rtl:rounded-bl-[5px]',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const attrs = computed(() => contentAttributes?.value || {});
// Metadado de ciclo de vida (editada/apagada). Sem faixa visual: a superfície
// A usa o fundo da própria mensagem e o selo no MessageLifecycle.
const filletTone = computed(() => lifecycleFilletTone(attrs.value));
const isRevoked = computed(() => isRevokedMessage(attrs.value));
const isEdited = computed(() => Boolean(attrs.value.edited));
const isPrivateNote = computed(
  () => variant.value === MESSAGE_VARIANTS.PRIVATE
);
const isBotMessage = computed(() => variant.value === MESSAGE_VARIANTS.BOT);

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(
      variant.value === MESSAGE_VARIANTS.PRIVATE
        ? 'rounded-xl'
        : orientationMap[orientation.value]
    );
  } else {
    classToApply.push('rounded-xl');
  }

  return classToApply;
});

const replyAuthorName = computed(() => {
  const sender = inReplyTo.value?.sender;
  if (!sender || typeof sender !== 'object') return '';

  const name = sender.name || sender.availableName || sender.available_name;
  return typeof name === 'string' ? name.trim() : '';
});

const replyQuoteLabel = computed(() => {
  if (replyAuthorName.value) {
    return t('CONVERSATION.SOFT_SURFACE.QUOTED_MESSAGE_WITH_AUTHOR', {
      author: replyAuthorName.value,
    });
  }

  return t('CONVERSATION.SOFT_SURFACE.QUOTED_MESSAGE');
});

const quoteAriaLabel = computed(() =>
  t('CONVERSATION.SOFT_SURFACE.QUOTE_GO_TO')
);

const scrollToMessage = () => {
  const messageId = inReplyTo.value?.id;
  if (!messageId) return;

  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
};

const onQuoteActivate = event => {
  if (event?.target?.closest?.('a')) return;
  if (event?.type === 'keydown') event.preventDefault();
  scrollToMessage();
};

const replyToPreview = computed(() => {
  if (!inReplyTo.value) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm min-w-0"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
    :data-lifecycle="filletTone || undefined"
  >
    <div
      v-if="isPrivateNote"
      data-testid="message-private-label"
      class="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-n-amber-11"
    >
      <span class="i-ph-lock-simple size-3.5 shrink-0" aria-hidden="true" />
      {{ t('CONVERSATION.SOFT_SURFACE.PRIVATE_NOTE') }}
    </div>
    <div
      v-if="isBotMessage"
      data-testid="message-bot-label"
      class="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-n-iris-11"
    >
      <span class="i-ph-sparkle size-3.5 shrink-0" aria-hidden="true" />
      {{ t('CONVERSATION.SOFT_SURFACE.AI_ASSISTANT') }}
    </div>
    <div
      v-if="inReplyTo"
      data-testid="message-reply-quote"
      class="mb-2 cursor-pointer rounded-lg bg-n-alpha-black1 p-2.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      role="button"
      tabindex="0"
      :aria-label="quoteAriaLabel"
      @click="onQuoteActivate"
      @keydown.enter="onQuoteActivate"
      @keydown.space="onQuoteActivate"
    >
      <span
        class="mb-1 flex items-center gap-1.5 text-[11px] font-semibold opacity-80"
      >
        <span
          class="i-ph-arrow-bend-up-left size-3.5 shrink-0"
          aria-hidden="true"
        />
        {{ replyQuoteLabel }}
      </span>
      <div
        v-dompurify-html="replyToPreview"
        class="prose prose-bubble line-clamp-2 text-[12px] opacity-90"
      />
    </div>
    <!-- M3: texto PRESERVADO: só baixa a ênfase (opacidade herda a cor da bolha). -->
    <div :class="{ 'opacity-70': isRevoked }" data-testid="message-bubble-body">
      <slot />
    </div>
    <MessageLifecycle
      :revoked-at="attrs.revokedAt || ''"
      :revoked-by="attrs.revokedBy || ''"
      :edited="isEdited"
      :edited-at="attrs.editedAt || ''"
      :original-content="attrs.originalContent || ''"
      :created-at="createdAt"
    />
  </div>
</template>
